import { Injectable } from "@angular/core"
import { BehaviorSubject, type Observable } from "rxjs"
import { FormState, HeaderState } from "../interfaces/IForm.interface";

@Injectable({
  providedIn: "root",
})
export class FormStateService {
  private formStateSubject = new BehaviorSubject<FormState>({ isValid: false })
  private actionSubject = new BehaviorSubject<string | null>(null)
  private headerStateSubject = new BehaviorSubject<HeaderState | null>(null);
  private printModeSubject = new BehaviorSubject<boolean>(false);

  /**
   * @description Updates the header state
   * @param {HeaderState} state - The new header state
   */
  updateHeaderState(state: HeaderState): void {
    this.headerStateSubject.next(state);
  }

  /**
   * @description Gets the header state as an observable
   * @returns {Observable<HeaderState>} Observable of the header state
   */
  getHeaderState(): Observable<HeaderState | null> {
    return this.headerStateSubject.asObservable();
  }

  /**
   * @description Updates the form state
   * @param {FormState} state - The new form state
   */
  updateFormState(state: FormState): void {
    this.formStateSubject.next(state)
  }

  /**
   * @description Gets the form state as an observable
   * @returns {Observable<FormState>} Observable of the form state
   */
  getFormState(): Observable<FormState> {
    return this.formStateSubject.asObservable()
  }

  /**
   * @description Triggers an action to be performed on the form
   * @param {string} action - The action to trigger (e.g., 'save', 'calculate')
   */
  triggerAction(action: string): void {
    this.actionSubject.next(action)
  }

  /**
   * @description Gets the action observable
   * @returns {Observable<string | null>} Observable of the action
   */
  getAction(): Observable<string | null> {
    return this.actionSubject.asObservable()
  }

  /**
   * @description Clears the current action
   */
  clearAction(): void {
    this.actionSubject.next(null)
  }

  /**
   * @description Sets the print mode state
   * @param {boolean} isPrintMode - Whether print mode is active
   */
  setPrintMode(isPrintMode: boolean): void {
    this.printModeSubject.next(isPrintMode)
  }

  /**
   * @description Gets the print mode state as an observable
   * @returns {Observable<boolean>} Observable of the print mode state
   */
  getPrintMode(): Observable<boolean> {
    return this.printModeSubject.asObservable()
  }
}

